import type { CSSProperties, ReactElement } from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { GAINS_EXEMPT_KINDS_PHRASE } from '@/domain/rollup'
import { formatMinor } from '~/api/client'
import type { KeyTexture } from '~/components/ui/Controls'
import { compactMinor, formatAxisDate } from './format'
import type { NetWorthPoint, ProvenanceSpan, SourceBeginning } from './series'
import { daysBetween } from '~/state/dates'
import {
  boundaryLabels,
  coverageBoundaries,
  coverageGaps,
  materialGaps,
  provenanceMarkerDates,
  provenanceOf,
  provenanceSentence,
  provenanceSpans,
} from './series'
import { COVERAGE_WASH_PERCENT, seriesColor } from './theme'

export interface Measure {
  key: 'lookThroughMinor' | 'spendableMinor' | 'liquidMinor'
  label: string
  slot: number
  note: string
  /**
   * Line style and hue together, never hue alone. Three weights of one green is
   * what the ramp gave, and a reader cannot tell a spendable line from a liquid
   * one at a glance; a dash pattern survives a glance, a grey-scale print and a
   * reader who cannot separate the two greens.
   */
  stroke: 'solid' | 'dashed' | 'dotted'
  width: number
  color: string
  /** Liquid starts off: the comparison the page is about is the first two. */
  onByDefault: boolean
  /**
   * Absent for a measure every household can read; named for one that answers
   * nothing until the household is shaped that way.
   */
  gate?: MeasureGate
  /**
   * A measure this one's note is written against, and what the note says
   * instead where that one is not offered. Liquid is "Spendable minus what
   * cannot be sold", and a household that is never shown Spendable is being
   * sent to a measure it has not got for the meaning of one it has.
   */
  definedAgainst?: { key: Measure['key']; note: string }
}

/** A measure that only means something once the household has something for it. */
export type MeasureGate = 'company' | 'unsellable'

export interface MeasureCapabilities {
  /** Whether anything is held through an entity other than the household itself. */
  company: boolean
  /**
   * Whether anything held cannot be sold. Without it Liquid subtracts nothing
   * and is the line above it drawn a third time (PRO-267).
   */
  unsellable: boolean
}

export const MEASURES: readonly Measure[] = [
  {
    key: 'lookThroughMinor',
    label: 'Look-through',
    slot: 0,
    note: 'Everything you own, attributed to you by share.',
    stroke: 'solid',
    width: 2.5,
    color: 'var(--nw-accent)',
    onByDefault: true,
  },
  {
    key: 'spendableMinor',
    label: 'Spendable',
    slot: 1,
    // What the charge spared is half of what this figure means, and it was the
    // half nothing said: a reader who is not taxed in Denmark was being shown a
    // larger number than they can take out, with nowhere to find out why.
    note: `After the tax it would cost to get the company money out. The gains charge is not applied to ${GAINS_EXEMPT_KINDS_PHRASE}.`,
    stroke: 'dashed',
    width: 2,
    color: 'var(--nw-muted)',
    onByDefault: true,
    gate: 'company',
  },
  {
    key: 'liquidMinor',
    label: 'Liquid',
    slot: 2,
    note: 'Spendable minus anything that cannot be sold.',
    definedAgainst: {
      key: 'spendableMinor',
      note: 'The part that could be sold without asking anyone.',
    },
    stroke: 'dotted',
    width: 1.5,
    color: 'var(--nw-faint)',
    onByDefault: false,
    // Liquid is the previous line less what cannot be sold. A household holding
    // nothing unsellable has nothing to take away, so the line would lie on top
    // of the one before it, and the key would offer the same answer twice.
    gate: 'unsellable',
  },
]

type Lean = Exclude<KeyTexture, 'flat'>

/**
 * The turn the plot gives a hatch's line, by the lean its key is drawn with.
 * Each hatch names its lean once and both read it, so one hatch's plot cannot
 * be turned while its key still leans the old way.
 */
const TURN: Record<Lean, number> = { rising: 45, falling: 135 }

/**
 * The two hatches, held as one record so the legend and the plot cannot come
 * to say different things.
 *
 * Told apart by hue as well as by lean, so that a reader who misses one still
 * has the other: carried forward wears the critical colour of its notice,
 * which the source's "Stale" pill already shows. It was `--nw-serious`, an
 * alias of `--nw-warning`, so the two used to differ by lean alone.
 */
export const HATCH = {
  modelled: { id: 'nw-hatch-modelled', color: 'var(--nw-warning)', texture: 'rising' },
  carriedForward: { id: 'nw-hatch-stale', color: 'var(--nw-critical)', texture: 'falling' },
} as const satisfies Record<ProvenanceSpan['kind'], { id: string; color: string; texture: Lean }>

/**
 * Enough ink to see, little enough to read the lines through. A run lasts as
 * long as a source stays down, and at a 7px pitch over an 8 % tint a figure
 * under one read as broken rather than qualified. The dots at each end of a
 * run, the notices and the table say what the ink no longer shouts.
 */
const HATCH_PITCH = 10
const HATCH_INK = 0.35

/**
 * Flat rather than hatched, and drawn behind everything: a stretch missing a
 * source is not a defect in the figures it holds, it is a smaller slice of the
 * household, and it must not read as the same kind of warning as a value nobody
 * confirmed.
 */
export const PARTIAL_COVERAGE_FILL = 'var(--nw-muted)'

/**
 * Along the foot, as a share of the axis: washed the full height, a stretch
 * with fewer sources took most of a year and read as the year being wrong, not
 * as a figure holding less. In the axis's units it costs the sheet no distance.
 */
const COVERAGE_STRIP = 0.04

/** A dash pattern each line keeps in the plot and in the key that names it. */
export const DASH: Record<Measure['stroke'], string | undefined> = {
  solid: undefined,
  dashed: '6 5',
  dotted: '1 4',
}

/**
 * The measures a household can read, with one gated on something it does not
 * have left out.
 *
 * `MEASURES` stays the whole list, the way `DESTINATIONS` stays the whole
 * routing truth and only the menu is filtered: a measure withheld here is still
 * a measure the rest of the product can name, price and explain.
 */
export function measuresFor(capabilities: MeasureCapabilities): readonly Measure[] {
  const offered = MEASURES.filter(
    (measure) => measure.gate === undefined || capabilities[measure.gate],
  )
  const shown = new Set(offered.map((measure) => measure.key))

  return offered.map((measure) =>
    measure.definedAgainst !== undefined && !shown.has(measure.definedAgainst.key)
      ? { ...measure, note: measure.definedAgainst.note }
      : measure,
  )
}

/** The measures drawn before anybody has touched the key. */
function defaultVisible(capabilities: MeasureCapabilities): ReadonlySet<string> {
  return new Set(
    measuresFor(capabilities)
      .filter((measure) => measure.onByDefault)
      .map((measure) => measure.key),
  )
}

export const DEFAULT_VISIBLE: ReadonlySet<string> = defaultVisible({ company: true, unsellable: true })

/**
 * The one line the page is about, and the only one with a wash under it.
 *
 * A fill under all three would close the gap between them, which is the single
 * thing this chart exists to show; under the first it says which line the eye
 * should start from.
 */
const FILLED = MEASURES[0] as Measure

export function NetWorthChart({
  points,
  baseCurrency,
  visible = DEFAULT_VISIBLE,
  measures = MEASURES,
  arrivals = [],
}: {
  points: readonly NetWorthPoint[]
  baseCurrency: string
  visible?: ReadonlySet<string>
  measures?: readonly Measure[]
  /** What each beginning in view brought, as the series measured it. */
  arrivals?: readonly SourceBeginning[]
}): ReactElement {
  const spans = provenanceSpans(points)
  const markers = provenanceMarkerDates(spans)
  const boundaries = coverageBoundaries(points)
  // The tooltip still counts the sources of every day short of one; only the
  // strip waits for a stretch whose missing sources would move the line.
  const missingByDate = missingSourcesByDate(points, coverageGaps(points))
  const marked = materialGaps(points, arrivals)
  const first = points[0]
  const last = points[points.length - 1]
  const spanDays = first && last ? daysBetween(first.date, last.date) : 0
  const labels = new Map(
    boundaryLabels(boundaries, arrivals, { from: first?.date ?? '', to: last?.date ?? '' }).map(
      (label) => [label.date, label],
    ),
  )

  const domainMax = niceMax(points, visible)
  const taxGap = gapBetween(points, visible)

  return (
    <div className="nw-plot-inner">
      {taxGap !== undefined && (
        <div
          className="nw-callout nw-callout--gap"
          style={{ top: `calc(${TOP}px + ${1 - taxGap.mid / domainMax} * (100% - ${TOP + AXIS}px))` } as CSSProperties}
        >
          <span className="nw-callout-label">The gap is tax on getting the company's money out</span>
          <span className="nw-callout-value">{formatMinor(taxGap.amountMinor, baseCurrency)}</span>
        </div>
      )}
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={points as NetWorthPoint[]} margin={{ top: 8, right: 34, bottom: 0, left: 8 }}>
        <defs>
          {Object.values(HATCH).map(({ id, color, texture }) => (
            <pattern
              key={id}
              id={id}
              width={HATCH_PITCH}
              height={HATCH_PITCH}
              patternTransform={`rotate(${TURN[texture]})`}
              patternUnits="userSpaceOnUse"
            >
              {/* Down the middle of its tile: a line on the tile's edge is cut
                  in half by it. */}
              <line
                x1={HATCH_PITCH / 2}
                y1="0"
                x2={HATCH_PITCH / 2}
                y2={HATCH_PITCH}
                stroke={color}
                strokeWidth="1"
                strokeOpacity={HATCH_INK}
              />
            </pattern>
          ))}
        </defs>

        {marked.map((gap) => (
          <ReferenceArea
            key={`coverage-${gap.from}`}
            x1={gap.from}
            x2={gap.to}
            y1={0}
            y2={domainMax * COVERAGE_STRIP}
            fill={PARTIAL_COVERAGE_FILL}
            fillOpacity={COVERAGE_WASH_PERCENT / 100}
            ifOverflow="hidden"
          />
        ))}

        <CartesianGrid stroke="var(--nw-grid)" strokeWidth={1} vertical={false} />
        <XAxis
          dataKey="date"
          axisLine={{ stroke: 'var(--nw-axis)' }}
          tickLine={false}
          minTickGap={48}
          tick={{ fill: 'var(--nw-muted)', fontSize: 12 }}
          tickFormatter={(date: string) => formatAxisDate(date, spanDays)}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          domain={[0, domainMax]}
          width={62}
          tick={{ fill: 'var(--nw-muted)', fontSize: 12 }}
          tickFormatter={compactMinor}
        />

        {visible.has(FILLED.key) && (
          <Area
            type="monotone"
            dataKey={FILLED.key}
            stroke="none"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
            tooltipType="none"
            legendType="none"
          />
        )}

        {spans.map((span) => (
          <ReferenceArea
            key={`${span.kind}-${span.from}`}
            x1={span.from}
            x2={span.to}
            fill={`url(#${HATCH[span.kind].id})`}
            fillOpacity={1}
            ifOverflow="hidden"
          />
        ))}

        {boundaries.map((boundary) => {
          const label = labels.get(boundary.date)
          return (
            <ReferenceLine
              key={`begins-${boundary.date}`}
              x={boundary.date}
              stroke="var(--nw-ink-2)"
              strokeDasharray="3 3"
              strokeWidth={1}
              ifOverflow="hidden"
              label={
                label && {
                  value: label.text,
                  // Named for the corners of a box, and a line's box has no
                  // width: "top left" reads rightwards from it.
                  position: label.side === 'right' ? 'insideTopLeft' : 'insideTopRight',
                  fill: 'var(--nw-ink-2)',
                  fontSize: 11,
                }
              }
            />
          )
        })}

        <Tooltip
          cursor={{ stroke: 'var(--nw-axis)', strokeWidth: 1 }}
          content={({ active, payload, label }) =>
            active ? (
              <NetWorthTooltip
                date={String(label)}
                point={payload?.[0]?.payload as NetWorthPoint | undefined}
                baseCurrency={baseCurrency}
                missing={missingByDate}
                measures={measures}
              />
            ) : null
          }
        />

        {measures.filter((measure) => visible.has(measure.key)).map((measure) => (
          <Line
            key={measure.key}
            type="monotone"
            dataKey={measure.key}
            name={measure.label}
            stroke={measure.color}
            strokeWidth={measure.width}
            strokeDasharray={DASH[measure.stroke]}
            strokeLinecap="round"
            strokeLinejoin="round"
            isAnimationActive={false}
            dot={(props: unknown) => {
              const dot = props as DotProps
              return <ProvenanceDot key={dot.key} {...dot} markers={markers} />
            }}
            activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--nw-card)' }}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
    </div>
  )
}

/**
 * The plot's own margins, named once so the callout can be put where the lines
 * actually are rather than where they look like they are.
 */
const TOP = 8
const AXIS = 30

/**
 * A top the axis can label roundly, and that the callout's arithmetic can rely
 * on. Left to Recharts the domain is chosen for the ticks and never reported,
 * so anything placed against it would be placed against a guess.
 */
function niceMax(points: readonly NetWorthPoint[], visible: ReadonlySet<string>): number {
  const values = points.flatMap((point) =>
    MEASURES.filter((measure) => visible.has(measure.key)).map((measure) => point[measure.key]),
  )
  const most = Math.max(1, ...values)
  const step = 10 ** Math.max(0, Math.floor(Math.log10(most)) - 1)
  return Math.ceil(most / step) * step
}

/**
 * The distance between what is owned and what could be spent, which is the tax
 * on getting the company's money out — the one thing this chart exists to show
 * and the one thing three lines of the same green never said.
 */
function gapBetween(
  points: readonly NetWorthPoint[],
  visible: ReadonlySet<string>,
): { mid: number; amountMinor: number } | undefined {
  if (!visible.has('lookThroughMinor') || !visible.has('spendableMinor')) return undefined
  const last = points[points.length - 1]
  if (last === undefined) return undefined
  const amountMinor = last.lookThroughMinor - last.spendableMinor
  if (amountMinor <= 0) return undefined
  return { mid: (last.lookThroughMinor + last.spendableMinor) / 2, amountMinor }
}

/**
 * Recharts hands the tooltip one point at a time, so the comparison against the
 * newest day in view has to be made once, up front, where the whole range is
 * still in hand.
 */
function missingSourcesByDate(
  points: readonly NetWorthPoint[],
  gaps: readonly { from: string; to: string; missing: string[] }[],
): ReadonlyMap<string, readonly string[]> {
  const missing = new Map<string, readonly string[]>()
  for (const gap of gaps) {
    for (const point of points) {
      if (point.date >= gap.from && point.date <= gap.to) missing.set(point.date, gap.missing)
    }
  }
  return missing
}

interface DotProps {
  cx?: number
  cy?: number
  stroke?: string
  key?: string
  payload?: { date?: string }
}

function ProvenanceDot({
  cx,
  cy,
  stroke,
  payload,
  markers,
}: DotProps & { markers: ReadonlySet<string> }): ReactElement {
  const date = payload?.date
  if (cx === undefined || cy === undefined || date === undefined || !markers.has(date)) {
    return <g />
  }
  return <circle cx={cx} cy={cy} r={4} fill={stroke} stroke="var(--nw-surface)" strokeWidth={2} />
}

function NetWorthTooltip({
  date,
  point,
  baseCurrency,
  missing,
  measures,
}: {
  date: string
  point: NetWorthPoint | undefined
  baseCurrency: string
  missing: ReadonlyMap<string, readonly string[]>
  measures: readonly Measure[]
}): ReactElement | null {
  if (!point) return null
  const provenance = provenanceOf(point)
  const absent = missing.get(point.date) ?? []

  return (
    <div className="nw-tooltip">
      <h3>{date}</h3>
      {absent.length > 0 && (
        <p className="nw-tooltip-coverage">
          {point.coverage?.sources.length ?? 0} of{' '}
          {(point.coverage?.sources.length ?? 0) + absent.length} sources. No history yet from{' '}
          {absent.join(', ')}.
        </p>
      )}
      <dl>
        {measures.map((measure) => (
          <div key={measure.key} style={{ display: 'contents' }}>
            <dt>
              <span
                className="nw-key nw-key--line"
                style={{ background: seriesColor(measure.slot) }}
              />
            </dt>
            <dd className="nw-tooltip-name">{measure.label}</dd>
            <dd className="nw-tooltip-value">{formatMinor(point[measure.key], baseCurrency)}</dd>
          </div>
        ))}
      </dl>
      {provenance.flagged && (
        <p className="nw-tooltip-flags">{provenanceSentence(point, baseCurrency)}</p>
      )}
    </div>
  )
}
